import type { CaseEntity, DisplayFieldValueEntity, EmployeeEntity } from '@features/cases';
import type { CasePrintData, CasePrintInvolvedEmployee } from '../interfaces/case-print-data.interface';

/**
 * Case Print Utilities
 *
 * Shared utilities for mapping case data to print format.
 * Used by dashboard, case detail, and other pages that need print functionality.
 */

/**
 * Maps a CaseEntity to CasePrintData for print preview.
 *
 * @param caseEntity - The case entity from the backend
 * @param displayFieldValues - Field values with display formatting
 * @param involvedEmployees - Optional array of involved employees
 * @returns Formatted data for print preview
 */
export function mapCaseToPrintData(
  caseEntity: CaseEntity,
  displayFieldValues: DisplayFieldValueEntity[],
  involvedEmployees?: EmployeeEntity[]
): CasePrintData {
  return {
    caseNumber: caseEntity.caseNumber,
    caseType: caseEntity.caseTypeName,
    category: caseEntity.caseTypeCategoryName,
    createdBy: caseEntity.createdByUserName,
    created: formatDateForPrint(caseEntity.createdOn),
    dueDate: caseEntity.dueDate ? formatDateForPrint(caseEntity.dueDate) : undefined,
    status: caseEntity.statusDisplayName,
    statusCategoryName: caseEntity.statusCategoryName,
    priority: caseEntity.priorityName,
    primaryAssignee: caseEntity.primaryAssigneeName || 'Unassigned',
    secondaryAssignee: caseEntity.secondaryAssigneeName || 'Unassigned',
    requestorName: caseEntity.requestedByName,
    requestorEmail: caseEntity.requestedByEmail,
    requestorPhone: caseEntity.requestedByPhone,
    requestorDivision: caseEntity.requestedByDivision,
    requestorDepartment: caseEntity.requestedByDepartment,
    involvedEmployees: mapInvolvedEmployees(involvedEmployees),
    resolutionNotes: caseEntity.closingNotes,
    description: extractDescription(displayFieldValues),
  };
}

/**
 * Maps involved employee entities to print format.
 *
 * @param employees - Optional array of involved employee entities
 * @returns Array of print-formatted involved employees
 */
export function mapInvolvedEmployees(employees?: EmployeeEntity[]): CasePrintInvolvedEmployee[] {
  if (!employees || employees.length === 0) {
    return [];
  }

  return employees.map((employee) => ({
    name: `${employee.firstName} ${employee.lastName}`,
    role: 'Involved Employee', // Generic role since relationshipType not available from backend
    email: employee.email,
  }));
}

/**
 * Formats an ISO date string to a human-readable format for printing.
 *
 * @param isoDate - ISO date string (e.g., "2026-05-06T10:30:00Z")
 * @returns Formatted date (e.g., "May 6, 2026")
 *
 * @example
 * formatDateForPrint('2026-05-18T02:18:47.185198-07:00') // "May 18, 2026"
 */
export function formatDateForPrint(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Extracts the description from display field values.
 * Looks for common description field labels like "Description", "Details", "Summary", etc.
 *
 * @param displayFieldValues - Array of display field values
 * @returns Description text or undefined if not found
 *
 * @example
 * const fieldValues = [
 *   { label: 'Description', value: 'Case details here' },
 *   { label: 'Status', value: 'Open' }
 * ];
 * extractDescription(fieldValues) // "Case details here"
 */
export function extractDescription(displayFieldValues: DisplayFieldValueEntity[]): string | undefined {
  const descriptionLabels = ['description', 'details', 'summary', 'notes', 'comments'];
  const descriptionField = displayFieldValues.find((field) =>
    descriptionLabels.some((label) => field.label.toLowerCase().includes(label))
  );
  return descriptionField?.value || undefined;
}
